import React, { useState } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Alert,
    Link,
    AppBar,
    Toolbar,
    Grid,
    Stepper,
    Step,
    StepLabel,
    FormControl,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";
import { authService } from "../../auth/authService";
import { LoggerService } from "../../services/LoggerService";
import ReCAPTCHA from "react-google-recaptcha";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../services/firebase";

const steps = ["basic_info", "kyc_basic", "role_preferences", "confirmation"];

const UnifiedSignup = () => {
    const navigate = useNavigate();
    const { loginWithGoogle, loginWithMicrosoft } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [recaptchaValue, setRecaptchaValue] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const { t } = useTranslation();

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
        setValue
    } = useForm({
        defaultValues: {
            email: "",
            password: "",
            confirmPassword: "",
            firstName: "",
            lastName: "",
            phone: "",
            userType: "individual",
            wantsToInvest: false,
            wantsToPublish: false,
            acceptTerms: false,
            acceptPrivacy: false,
            acceptMarketing: false
        }
    });

    const password = watch("password");
    const formData = watch();

    const validateStep = (step) => {
        switch (step) {
        case 0: // basic_info
            return formData.email && formData.password && formData.confirmPassword && 
                       formData.firstName && formData.lastName && recaptchaValue;
        case 1: // kyc_basic
            return formData.phone && formData.userType;
        case 2: // role_preferences
            return formData.wantsToInvest || formData.wantsToPublish;
        case 3: // confirmation
            return formData.acceptTerms && formData.acceptPrivacy;
        default:
            return false;
        }
    };

    const handleNext = () => {
        if (validateStep(activeStep)) {
            setActiveStep((prevStep) => prevStep + 1);
            setError("");
        } else {
            setError(t("pleaseCompleteAllFields"));
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
        setError("");
    };

    const onSubmit = async (data) => {
        try {
            setError("");
            setLoading(true);

            // Log registration attempt
            LoggerService.log("auth", "registration_started", {
                email: data.email,
                userType: data.userType,
                wantsToInvest: data.wantsToInvest,
                wantsToPublish: data.wantsToPublish
            });

            // Create user with email verification
            const userCred = await authService.signupWithEmailVerification(
                data.email,
                data.password,
                `${data.firstName} ${data.lastName}`
            );

            // Update user profile with additional data
            const userRef = doc(db, "users", userCred.user.uid);
            await updateDoc(userRef, {
                firstName: data.firstName,
                lastName: data.lastName,
                phone: data.phone,
                userType: data.userType,
                wantsToInvest: data.wantsToInvest,
                wantsToPublish: data.wantsToPublish,
                "roles.investor": data.wantsToInvest,
                "roles.agent": data.wantsToPublish,
                acceptTerms: data.acceptTerms,
                acceptPrivacy: data.acceptPrivacy,
                acceptMarketing: data.acceptMarketing,
                updatedAt: new Date().toISOString()
            });

            // Log successful registration
            LoggerService.log("auth", "registration_completed", {
                userId: userCred.user.uid,
                email: data.email
            });

            setShowConfirmation(true);

        } catch (err) {
            LoggerService.log("auth", "registration_failed", {
                error: err.message,
                email: data.email
            });
            setError(t("signupError"));
        } finally {
            setLoading(false);
        }
    };

    const handleSocialLogin = async (provider) => {
        try {
            setError("");
            setLoading(true);

            let result;
            if (provider === "google") {
                result = await loginWithGoogle();
            } else if (provider === "microsoft") {
                result = await loginWithMicrosoft();
            }

            LoggerService.log("auth", "social_login_success", {
                provider,
                userId: result.user.uid
            });

            navigate("/dashboard");
        } catch (err) {
            LoggerService.log("auth", "social_login_failed", {
                provider,
                error: err.message
            });
            setError(t(`${provider}SignUpError`));
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
        case 0:
            return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{ required: t("firstNameRequired") }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("firstName")}
                                error={!!errors.firstName}
                                helperText={errors.firstName?.message}
                            />
                        )}
                    />
                    <Controller
                        name="lastName"
                        control={control}
                        rules={{ required: t("lastNameRequired") }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("lastName")}
                                error={!!errors.lastName}
                                helperText={errors.lastName?.message}
                            />
                        )}
                    />
                    <Controller
                        name="email"
                        control={control}
                        rules={{
                            required: t("emailRequired"),
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t("invalidEmail")
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("email")}
                                type="email"
                                error={!!errors.email}
                                helperText={errors.email?.message}
                            />
                        )}
                    />
                    <Controller
                        name="password"
                        control={control}
                        rules={{
                            required: t("passwordRequired"),
                            minLength: {
                                value: 8,
                                message: t("passwordMinLength")
                            }
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("password")}
                                type="password"
                                error={!!errors.password}
                                helperText={errors.password?.message}
                            />
                        )}
                    />
                    <Controller
                        name="confirmPassword"
                        control={control}
                        rules={{
                            required: t("confirmPasswordRequired"),
                            validate: value => value === password || t("passwordsDoNotMatch")
                        }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("confirmPassword")}
                                type="password"
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message}
                            />
                        )}
                    />
                    <ReCAPTCHA
                        sitekey="6LfZTm4rAAAAAF-GuvQpPWOPhqcENL9NHbi56_JI"
                        onChange={value => setRecaptchaValue(value)}
                    />
                </Box>
            );

        case 1:
            return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Controller
                        name="phone"
                        control={control}
                        rules={{ required: t("phoneRequired") }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={t("phone")}
                                type="tel"
                                error={!!errors.phone}
                                helperText={errors.phone?.message}
                            />
                        )}
                    />
                    <Controller
                        name="userType"
                        control={control}
                        rules={{ required: t("userTypeRequired") }}
                        render={({ field }) => (
                            <FormControl fullWidth>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value === "individual"}
                                            onChange={() => setValue("userType", "individual")}
                                        />
                                    }
                                    label={t("individual")}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={field.value === "corporate"}
                                            onChange={() => setValue("userType", "corporate")}
                                        />
                                    }
                                    label={t("corporate")}
                                />
                            </FormControl>
                        )}
                    />
                </Box>
            );

        case 2:
            return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t("whatWouldYouLikeToDo")}
                    </Typography>
                    <Controller
                        name="wantsToInvest"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => setValue("wantsToInvest", e.target.checked)}
                                    />
                                }
                                label={t("iWantToInvest")}
                            />
                        )}
                    />
                    <Controller
                        name="wantsToPublish"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => setValue("wantsToPublish", e.target.checked)}
                                    />
                                }
                                label={t("iWantToPublishOpportunities")}
                            />
                        )}
                    />
                </Box>
            );

        case 3:
            return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        {t("reviewYourInformation")}
                    </Typography>
                    <Box sx={{ p: 2, border: 1, borderColor: "grey.300", borderRadius: 1 }}>
                        <Typography><strong>{t("name")}:</strong> {formData.firstName} {formData.lastName}</Typography>
                        <Typography><strong>{t("email")}:</strong> {formData.email}</Typography>
                        <Typography><strong>{t("phone")}:</strong> {formData.phone}</Typography>
                        <Typography><strong>{t("userType")}:</strong> {t(formData.userType)}</Typography>
                        <Typography><strong>{t("preferences")}:</strong> {
                            [formData.wantsToInvest && t("invest"), formData.wantsToPublish && t("publish")]
                                .filter(Boolean).join(", ")
                        }</Typography>
                    </Box>
                    <Controller
                        name="acceptTerms"
                        control={control}
                        rules={{ required: t("termsRequired") }}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => setValue("acceptTerms", e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {t("iAccept")} <Link href="/terms">{t("termsAndConditions")}</Link>
                                    </Typography>
                                }
                            />
                        )}
                    />
                    <Controller
                        name="acceptPrivacy"
                        control={control}
                        rules={{ required: t("privacyRequired") }}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => setValue("acceptPrivacy", e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {t("iAccept")} <Link href="/privacy">{t("privacyPolicy")}</Link>
                                    </Typography>
                                }
                            />
                        )}
                    />
                    <Controller
                        name="acceptMarketing"
                        control={control}
                        render={({ field }) => (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => setValue("acceptMarketing", e.target.checked)}
                                    />
                                }
                                label={t("iAcceptMarketingEmails")}
                            />
                        )}
                    />
                </Box>
            );

        default:
            return null;
        }
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography
                        variant="h6"
                        component="div"
                        onClick={() => navigate("/")}
                        sx={{
                            flexGrow: 1,
                            background: "linear-gradient(90deg, #DBC078 0%, #d0ad50 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            fontWeight: 900,
                            letterSpacing: 2,
                            cursor: "pointer",
                            "&:hover": {
                                opacity: 0.8
                            }
                        }}
                    >
                        CAPIVIO
                    </Typography>
                    <LanguageSwitcher />
                </Toolbar>
            </AppBar>

            <Container maxWidth="md">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Paper elevation={3} sx={{ p: 4, width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
                                <Typography variant="h4" component="h1" align="center" gutterBottom>
                                    {t("createAccount")}
                                </Typography>

                                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                                    {steps.map((label) => (
                                        <Step key={label}>
                                            <StepLabel>{t(label)}</StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>

                                {error && <Alert severity="error">{error}</Alert>}

                                <form onSubmit={handleSubmit(onSubmit)}>
                                    {renderStepContent(activeStep)}

                                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
                                        <Button
                                            disabled={activeStep === 0}
                                            onClick={handleBack}
                                        >
                                            {t("back")}
                                        </Button>
                                        <Box>
                                            {activeStep === steps.length - 1 ? (
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    disabled={loading}
                                                    startIcon={loading && <CircularProgress size={20} />}
                                                >
                                                    {loading ? t("creatingAccount") : t("createAccount")}
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    onClick={handleNext}
                                                    disabled={!validateStep(activeStep)}
                                                >
                                                    {t("next")}
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </form>

                                {activeStep === 0 && (
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            fullWidth
                                            onClick={() => handleSocialLogin("google")}
                                            disabled={loading}
                                        >
                                            {t("signUpWithGoogle")}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            fullWidth
                                            onClick={() => handleSocialLogin("microsoft")}
                                            disabled={loading}
                                        >
                                            {t("signUpWithMicrosoft")}
                                        </Button>
                                    </Box>
                                )}

                                <Box sx={{ textAlign: "center" }}>
                                    <Typography variant="body2">
                                        {t("alreadyHaveAccount")}{" "}
                                        <Link href="/login" underline="hover">
                                            {t("login")}
                                        </Link>
                                    </Typography>
                                </Box>
                            </Paper>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Paper sx={{ p: 4, borderRadius: 4, mb: 3, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: "#111", mb: 2 }}>
                                {t("joinCAPIVIO")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t("createAccountDescription")}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>

            <Dialog open={showConfirmation} onClose={() => setShowConfirmation(false)}>
                <DialogTitle>{t("accountCreated")}</DialogTitle>
                <DialogContent>
                    <Typography>
                        {t("verificationEmailSent")}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => navigate("/login")}>
                        {t("goToLogin")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UnifiedSignup; 